import React, { useCallback, useEffect, useState } from 'react';

const SECTION_THEMES = {
    overview: 'primary',
    patients: 'indigo',
    appointments: 'purple',
    communications: 'success',
    gateway: 'cyan',
    bookings: 'teal',
    campaigns: 'orange',
    recalls: 'warning',
    'reputation-enhancement': 'pink',
    pms: 'info',
    staff: 'slate',
};

function DashboardSectionNav({ sections, activeSection }) {
    const [currentSection, setCurrentSection] = useState(activeSection || sections[0]?.id);

    const scrollToSection = useCallback((sectionId) => {
        const element = document.getElementById(`dashboard-section-${sectionId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentSection(sectionId);
        }
    }, []);

    useEffect(() => {
        const sectionElements = sections
            .map((section) => document.getElementById(`dashboard-section-${section.id}`))
            .filter(Boolean);

        if (!sectionElements.length) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visible[0]?.target?.id) {
                    setCurrentSection(visible[0].target.id.replace('dashboard-section-', ''));
                }
            },
            { rootMargin: '-120px 0px -55% 0px', threshold: [0.15, 0.35, 0.6] }
        );

        sectionElements.forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, [sections]);

    return (
        <nav className="dashboard-section-nav" aria-label="Dashboard sections">
            <div className="dashboard-section-nav__track">
                {sections.map((section) => {
                    const theme = SECTION_THEMES[section.id] || 'primary';
                    const isActive = currentSection === section.id;

                    return (
                        <button
                            key={section.id}
                            type="button"
                            className={`dashboard-section-nav__pill dashboard-section-nav__pill--${theme} ${
                                isActive ? 'is-active' : ''
                            }`}
                            onClick={() => scrollToSection(section.id)}
                            aria-current={isActive ? 'true' : undefined}
                        >
                            <i className={`bi ${section.icon}`} aria-hidden="true" />
                            <span>{section.title}</span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}

export default DashboardSectionNav;
